import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {object} param
 * @param {FullAirport} param.airport
 * @returns {{
 * AIRPORT_FORM_SCHEMA: import('@zolteam/zol-react-form/types').BasicSchema,
 * airportDefaultValues: object,
 * splitAirportForm: Function,
 * airportFormDataToPayload: Function
 * }}
 */
export const useAirports = ({ airport }) => {
  const { t } = useTranslation();

  const AIRPORT_FORM_SCHEMA = useMemo(() => ({
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Airport',
    properties: {
      ident: {
        type: 'string',
        description: t('airportForm.fields.ident'),
        x_layout: '1/1/1/10',
      },
      name: {
        type: 'string',
        description: t('airportForm.fields.name'),
        x_layout: '1/1/1/10',
        minLength: '1',
      },
      code: {
        type: 'string',
        description: t('airportForm.fields.code'),
        x_layout: '2/1/2/5',
        minLength: '1',
      },
      municipality: {
        type: 'string',
        description: t('airportForm.fields.municipality'),
        x_layout: '2/6/2/10',
      },
      city: {
        type: 'string',
        description: t('airportForm.fields.city'),
        x_layout: '2/6/2/10',
        minLength: '1',
      },
      coordinate: {
        type: 'string',
        description: t('terminalForm.fields.coordinate'),
        x_layout: '3/1/3/10',
      },
      isoCountry: {
        type: 'string',
        description: t('airportForm.fields.isoCountry'),
        x_layout: '4/1/4/5',
      },
      iataCode: {
        type: 'string',
        description: t('airportForm.fields.iataCode'),
        x_layout: '4/6/4/10',
      },
    },
    required: ['name', 'code', 'city'],
  }), [t]);

  const airportDefaultValues = useMemo(() => ({
    name: airport?.name || '',
    code: airport?.code || '',
    ident: airport?.ident || '',
    city: airport?.city || '',
    terminals: airport?.terminals || [],
    municipality: airport?.municipality || '',
    // compute coordinate default: prefer airport.coordinate array, fallback to latitudeDeg/longitudeDeg
    coordinate: (() => {
      const a = /** @type {any} */ (airport);
      if (a && Array.isArray(a.coordinate) && a.coordinate.length) {
        return a.coordinate.join(',');
      }
      if (airport?.latitudeDeg || airport?.longitudeDeg) {
        return `${airport?.latitudeDeg || ''},${airport?.longitudeDeg || ''}`;
      }
      return '';
    })(),
    isoCountry: airport?.isoCountry || '',
    iataCode: airport?.iataCode || '',
  }), [airport]);

  /**
   * @param {object} params - The values from the form.
   * @param {any} params.formValues - The values from the form.
   * @returns {FullAirport} Payload
   */
  const airportFormDataToPayload = ({
    formValues,
  }) => ({
    ident: formValues.ident || airport?.ident || '',
    code: formValues.code,
    name: formValues.name,
    city: formValues.city,
    municipality: formValues.municipality || null,
    // parse `coordinate` if provided as "lat,lon" (string) or an array
    ...(function parseCoordinate(coord) {
      let lat = null;
      let lon = null;
      if (coord) {
        if (typeof coord === 'string') {
          const parts = coord.split(',').map((p) => p.trim());
          lat = parts[0] || null;
          lon = parts[1] || null;
        } else if (Array.isArray(coord)) {
          lat = coord[0] || null;
          lon = coord[1] || null;
        }
      }
      return { latitudeDeg: lat, longitudeDeg: lon };
    }(formValues.coordinate)),
    isoCountry: formValues.isoCountry || null,
    iataCode: formValues.iataCode || null,
    terminals: formValues.terminals || [],
  });

  /**
   * Split the airport form into different parts
   * @param {import('@zolteam/zol-react-form/types').ConfigObject} formConfig
   * @returns {{ generalPart: object, terminalsPart: object }} The split airport form
   */
  const splitAirportForm = (formConfig) => Object.entries(formConfig)
    .reduce((acc, [key, value]) => {
      if (key?.includes('terminals')) {
        return { ...acc, terminalsPart: { ...acc.terminalsPart, [key]: value } };
      }
      return { ...acc, generalPart: { ...acc.generalPart, [key]: value } };
    }, {
      generalPart: {},
      terminalsPart: {},
    });

  return {
    AIRPORT_FORM_SCHEMA,
    airportDefaultValues,
    splitAirportForm,
    airportFormDataToPayload,
  };
};
